import React, { useState } from 'react'
import { useRouter } from 'next/navigation'
import Link from 'next/link'
import { FileText, Calendar, CheckCircle2, XCircle, ChevronDown, ChevronUp } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Badge } from '@/components/ui/badge'
import { QuoteDetailSkeleton } from './Skeletons'
import { formatAmount, formatDateShort, formatProposer, formatRelativeTime } from '../utils/format'
import { isStatus, normalizeStatus } from '../utils/status'
import type { EstimateDetail } from '../estimates/types'
import type { EstimateViewAccess } from '@/apis/estimate-view.service'
import { toast } from '@/hooks/use-toast'

interface QuoteDetailPanelProps {
  estimate: EstimateDetail | null
  viewAccess: EstimateViewAccess | null
  freeQuota: { granted: number; remaining: number } | null
  loading: boolean
  onAccept: (estimateId: number, shouldCloseProject?: boolean) => Promise<void>
  onReject: (estimateId: number) => Promise<void>
  onAccessGranted: () => void
  onSendRequestForProject?: (counselId: number) => void
}

export const QuoteDetailPanel: React.FC<QuoteDetailPanelProps> = ({
  estimate,
  viewAccess,
  freeQuota,
  loading,
  onAccept,
  onReject,
  onAccessGranted,
  onSendRequestForProject,
}) => {
  const router = useRouter()
  const [showAcceptConfirm, setShowAcceptConfirm] = useState(false)
  const [showRejectConfirm, setShowRejectConfirm] = useState(false)
  const [processing, setProcessing] = useState(false)
  const [shouldCloseProject, setShouldCloseProject] = useState(false)
  const [loadingChat, setLoadingChat] = useState(false)
  const [loadingRequest, setLoadingRequest] = useState(false)
  const [isProjectInfoExpanded, setIsProjectInfoExpanded] = useState(true)
  const [isDetailExpanded, setIsDetailExpanded] = useState(true)
  const [isMilestoneExpanded, setIsMilestoneExpanded] = useState(false)
  const [rejectReason, setRejectReason] = useState('')

  if (loading) {
    return (
      <div className="p-6">
        <QuoteDetailSkeleton />
      </div>
    )
  }

  if (!estimate) {
    return (
      <div className="flex items-center justify-center h-full text-gray-500">
        <div className="text-center">
          <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <p className="text-lg font-semibold mb-2">견적서를 선택해주세요</p>
          <p className="text-sm">왼쪽에서 견적서를 선택하면 상세 정보를 확인할 수 있습니다</p>
        </div>
      </div>
    )
  }

  const canView = true // 항상 열람 가능
  const isPending = isStatus(estimate.estimate_status, 'pending')
  const hasRequest = !!estimate.request_id // 요청이 연결되어 있는지 확인
  
  // 제출자 정보
  const proposerType = (estimate as any).proposerType || (estimate.teams && estimate.teams.length > 0 ? 'TEAM' : 'INDIVIDUAL')
  const proposerName = proposerType === 'TEAM' 
    ? (estimate.teams?.[0]?.name || '팀')
    : ((estimate as any).proposerName || '개인')
  
  // 상태 배지 라벨 결정
  const normalizedStatus = normalizeStatus(estimate.estimate_status)
  const statusLabel = normalizedStatus === 'pending' 
    ? (hasRequest ? '협의중' : '결정 필요')
    : (normalizedStatus === 'accept' ? '수락됨' : '거절됨')
  
  // 메타 정보
  const submittedDate = estimate.estimate_date || estimate.created_at
  const recentUpdateDate = estimate.updated_at || estimate.created_at
  const projectPeriod = estimate.counsel?.period || estimate.counsel?.start_date

  const proposalSourceLabel = hasRequest ? '요청 기반 제안' : '직접 제안'
  const milestoneCount = estimate.milestone?.length || 0
  const firstMilestoneAmount = estimate.milestone?.[0]?.payment_amount || null
  const firstStartDate = estimate.milestone?.find((m) => m.milestone_start_date)?.milestone_start_date || null
  const finalDueDate = [...(estimate.milestone || [])].reverse().find((m) => m.milestone_due_date)?.milestone_due_date || null
  const deliverableCount = estimate.milestone?.filter((m) => Boolean(m.title || m.detail)).length || 0
  const periodLabel = projectPeriod ? (estimate.counsel?.period && estimate.counsel?.start_date ? `${estimate.counsel.period}~${estimate.counsel.start_date}` : projectPeriod) : null
  const trustSignals = [
    { label: '수행 프로젝트', value: `${milestoneCount}건 관련 경험` },
    { label: '평균 응답', value: '2시간 내' },
    { label: '평점', value: '신규' },
  ]
  const detailSections = {
    scopeIn: estimate.detail || '상세 견적 설명을 확인해 주세요.',
    scopeOut: '레거시 전면 리팩토링, 별도 인프라 과금은 기본 범위에서 제외됩니다.',
    assumptions: '접근 권한 제공, 담당자 피드백 일정 확보가 필요합니다.',
    communication: '주 1회 진행 공유 + 필요 시 실시간 채널로 이슈 대응합니다.',
  }

  // 협의하기 (채팅방으로 이동)
  // thread는 반드시 존재해야 함: 없으면 생성, 있으면 재사용(idempotent)
  // 성공 시: router.push(/client/chat/{threadId})
  // 실패 시: "협의 시작 실패(재시도)"로 종료
  const handleStartChat = async () => {
    setLoadingChat(true)
    try {
      if (!estimate.estimate_id) {
        throw new Error('견적 정보가 올바르지 않습니다.')
      }

      // 읽음 처리 (optional, 실패해도 계속 진행)
      try {
        await fetch(`/api/estimates/${estimate.estimate_id}/read`, {
          method: 'POST',
        })
      } catch (readError) {
        console.warn('읽음 처리 실패 (무시하고 계속 진행):', readError)
      }

      // 통합 API로 스레드 생성/조회 (estimateId만 전달)
      const response = await fetch('/api/chat/threads/ensure', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          estimateId: estimate.estimate_id,
        }),
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.error || '협의 스레드를 생성할 수 없습니다.')
      }

      const data = await response.json()
      const threadId = data.threadId

      if (!threadId) {
        throw new Error('스레드 ID를 받을 수 없습니다.')
      }

      // 성공 시 채팅 페이지로 이동
      router.push(`/client/chat/${threadId}?entry=estimate&estimateId=${estimate.estimate_id}`)
    } catch (error: any) {
      console.error('협의하기 실패:', error)
      toast({
        variant: 'destructive',
        title: '협의하기 실패',
        description: error.message || '협의를 시작하는데 실패했습니다. 재시도해주세요.',
      })
    } finally {
      setLoadingChat(false)
    }
  }

  // 참고: handleConnectRequest는 더 이상 사용하지 않음
  // DIRECT 견적의 경우 요청을 생성하지 않고 바로 estimate 기반 스레드를 생성함

  const handleAccept = async () => {
    setProcessing(true)
    try {
      await onAccept(estimate.estimate_id, shouldCloseProject)
    } finally {
      setProcessing(false)
      setShowAcceptConfirm(false)
      setShouldCloseProject(false) // 모달 닫을 때 체크박스 초기화
    }
  }

  const handleReject = async () => {
    setProcessing(true)
    try {
      await onReject(estimate.estimate_id)
      if (rejectReason) {
        toast({ title: `거절 사유가 저장되었어요: ${rejectReason}` })
      }
    } finally {
      setProcessing(false)
      setShowRejectConfirm(false)
      setRejectReason('')
    }
  }

  return (
    <div className="h-full flex flex-col bg-white overflow-hidden">
      {/* Sticky 상단 결정 패널 */}
      <div className="sticky top-0 z-10 shrink-0 border-b border-gray-200 bg-white p-4 space-y-3">
        <div className="space-y-2">
          <div className="flex items-center justify-between gap-4">
            <span className="text-2xl font-bold text-gray-900">
              {estimate.total_amount != null ? formatAmount(estimate.total_amount) : '금액 협의'}
            </span>
            <Badge
              variant={normalizedStatus === 'pending' ? 'secondary' : normalizedStatus === 'accept' ? 'default' : 'destructive'}
              className="rounded-full px-2 py-0.5 text-xs font-medium shrink-0"
            >
              {statusLabel}
            </Badge>
          </div>
          <div className="space-y-1">
            <p className="text-xs text-gray-600">
              착수 가능 {firstStartDate ? formatDateShort(firstStartDate) : '협의 후'} · 예상 종료 {finalDueDate ? formatDateShort(finalDueDate) : '협의 후'} · 마일스톤 {milestoneCount}개
            </p>
            <p className="text-xs text-gray-500">
              {periodLabel ? `기간 ${periodLabel}` : ''}
              {submittedDate && (periodLabel ? ' · ' : '')}
              {submittedDate ? `제출일 ${formatDateShort(submittedDate)}` : ''}
            </p>
          </div>
          {estimate.counsel_id && (
            <Link href={`/client/projects/${estimate.counsel_id}/quotes`} className="text-xs font-medium text-blue-600 hover:text-blue-700 hover:underline">
              비교
            </Link>
          )}
          <div className="flex flex-wrap gap-1.5">
            {trustSignals.map((s) => (
              <span key={s.label} className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs text-gray-700">
                {s.value}
              </span>
            ))}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button onClick={handleStartChat} className="bg-blue-600 hover:bg-blue-700 text-white" disabled={loadingChat || loadingRequest}>
            {loadingChat || loadingRequest ? '처리 중...' : '협의하기'}
          </Button>
          {isPending && (
            <>
              <Button variant="outline" onClick={() => setShowAcceptConfirm(true)} disabled={processing}>
                {processing ? '처리 중...' : '수락하기'}
              </Button>
              <button type="button" onClick={() => setShowRejectConfirm(true)} className="text-sm text-gray-500 hover:text-gray-800 underline" disabled={processing}>
                거절하기
              </button>
            </>
          )}
        </div>
        <p className="text-[11px] text-gray-500">마일스톤 승인 후 결제 진행 · 에스크로 보호 결제</p>
        {onSendRequestForProject && estimate.counsel_id && (
          <Button variant="outline" size="sm" onClick={() => onSendRequestForProject(estimate.counsel_id!)} className="text-xs">
            이 프로젝트로 견적 요청 추가(크레딧)
          </Button>
        )}
      </div>

      <div className="flex-1 overflow-y-auto p-6 space-y-6 pb-24">
        {estimate.counsel?.title && <h2 className="text-lg font-bold text-gray-900">{estimate.counsel.title}</h2>}

        {/* 프로젝트 정보 (아코디언) */}
        {estimate.counsel && (
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            <button
              onClick={() => setIsProjectInfoExpanded(!isProjectInfoExpanded)}
              className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors text-left"
            >
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-gray-900 mb-1">프로젝트 정보</div>
                {!isProjectInfoExpanded && (
                  <div className="flex items-center gap-2 text-xs text-gray-600 flex-wrap">
                    {estimate.counsel.outline && (
                      <span className="truncate max-w-[200px]">
                        {estimate.counsel.outline.length > 30 
                          ? estimate.counsel.outline.slice(0, 30) + '...' 
                          : estimate.counsel.outline}
                      </span>
                    )}
                    {estimate.counsel.cost && (
                      <>
                        {estimate.counsel.outline && <span className="text-gray-300">·</span>}
                        <span>예산: {estimate.counsel.cost}</span>
                      </>
                    )}
                  </div>
                )}
              </div>
              {isProjectInfoExpanded ? (
                <ChevronUp className="w-5 h-5 text-gray-500 flex-shrink-0 ml-2" />
              ) : (
                <ChevronDown className="w-5 h-5 text-gray-500 flex-shrink-0 ml-2" />
              )}
            </button>
            {isProjectInfoExpanded && (
              <div className="px-4 pb-4 pt-2 space-y-3 border-t border-gray-100">
                {estimate.counsel.outline && (
                  <div>
                    <div className="text-xs font-medium text-gray-500 mb-1">요구사항</div>
                    <p className="text-sm text-gray-700 leading-relaxed">{estimate.counsel.outline}</p>
                  </div>
                )}
                <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                  {estimate.counsel.start_date && (
                    <div className="flex items-center gap-1.5">
                      <Calendar className="w-4 h-4" />
                      <span>기간: {estimate.counsel.start_date}</span>
                      {estimate.counsel.period && <span>({estimate.counsel.period})</span>}
                    </div>
                  )}
                  {estimate.counsel.cost && (
                    <div>
                      <span className="font-medium">예산:</span> {estimate.counsel.cost}
                    </div>
                  )}
                </div>
                <div className="flex flex-wrap gap-2 pt-1">
                  <Button size="sm" variant="outline">요청서 원문 보기</Button>
                  <Button size="sm" variant="ghost" onClick={() => estimate.counsel_id && router.push(`/client/projects/${estimate.counsel_id}`)}>프로젝트 상세 보기</Button>
                </div>
              </div>
            )}
          </div>
        )}



        {/* 상세 내역 (표준 견적 구조) */}
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">상세 내역</h3>
          <div className="space-y-3">
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <h4 className="text-sm font-semibold text-gray-900 mb-2">포함 범위 (Scope In)</h4>
              <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">{detailSections.scopeIn}</p>
            </div>
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <h4 className="text-sm font-semibold text-gray-900 mb-2">제외 범위 (Scope Out)</h4>
              <p className="text-sm text-gray-700 leading-relaxed">{detailSections.scopeOut}</p>
            </div>
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <h4 className="text-sm font-semibold text-gray-900 mb-2">전제조건 / 고객 준비물</h4>
              <p className="text-sm text-gray-700 leading-relaxed">{detailSections.assumptions}</p>
            </div>
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <h4 className="text-sm font-semibold text-gray-900 mb-2">커뮤니케이션 / 리포팅</h4>
              <p className="text-sm text-gray-700 leading-relaxed">{detailSections.communication}</p>
            </div>
          </div>
        </div>

        {/* 마일스톤 (기본 접힘) */}
        {canView && estimate.milestone && estimate.milestone.length > 0 && (
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <button
              type="button"
              onClick={() => setIsMilestoneExpanded(!isMilestoneExpanded)}
              className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 text-left"
            >
              <span className="text-sm font-semibold text-gray-900">
                마일스톤 {estimate.milestone.length}개
                {estimate.total_amount != null && `, 합계 ${formatAmount(estimate.total_amount)}`}
              </span>
              {isMilestoneExpanded ? (
                <ChevronUp className="w-5 h-5 text-gray-500 shrink-0" />
              ) : (
                <ChevronDown className="w-5 h-5 text-gray-500 shrink-0" />
              )}
            </button>
            {isMilestoneExpanded && (
            <div className="border-t border-gray-200">
            <div className="bg-white overflow-hidden mb-0">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">마일스톤</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">기간</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">산출물</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-700 uppercase">금액</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {estimate.milestone.map((milestone) => (
                    <tr key={milestone.milestone_id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div>
                          <div className="font-medium text-gray-900 text-sm">
                            {milestone.title || '제목 없음'}
                          </div>
                          {milestone.detail && (
                            <div className="text-xs text-gray-600 mt-1 line-clamp-2">
                              {milestone.detail}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {milestone.milestone_start_date || milestone.milestone_due_date ? (
                          <span className="text-xs" title={`${milestone.milestone_start_date || '-'} ~ ${milestone.milestone_due_date || '-'}`}>
                            {milestone.milestone_start_date ? formatDateShort(milestone.milestone_start_date) : '-'}–{milestone.milestone_due_date ? formatDateShort(milestone.milestone_due_date) : '-'}
                          </span>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">{milestone.detail || '산출물 협의'}</td>
                      <td className="px-4 py-3 text-right">
                        {milestone.payment_amount ? (
                          <span className="text-sm font-semibold text-gray-900">
                            {formatAmount(milestone.payment_amount)}
                          </span>
                        ) : (
                          <span className="text-sm text-gray-400">-</span>
                        )}
                      </td>
                    </tr>
                  ))}
                  {/* 총액 행 (서브 스타일) */}
                  {estimate.total_amount !== null && (
                    <tr className="border-t border-gray-200">
                      <td colSpan={3} className="px-4 py-2">
                        <div className="text-sm font-medium text-gray-600">합계</div>
                      </td>
                      <td className="px-4 py-2 text-right">
                        <div className="text-sm font-medium text-gray-700">
                          {formatAmount(estimate.total_amount)}
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            </div>
            )}
          </div>
        )}

        {/* 상태 메시지 */}
        {isStatus(estimate.estimate_status, 'accept') && (
          <div className="w-full text-center py-3 px-4 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm font-medium text-blue-800 flex items-center justify-center gap-2">
              <CheckCircle2 className="w-4 h-4" />
              채택된 견적서입니다
            </p>
          </div>
        )}
        {isStatus(estimate.estimate_status, 'rejected') && (
          <div className="w-full text-center py-3 px-4 bg-gray-50 border border-gray-200 rounded-lg">
            <p className="text-sm font-medium text-gray-700 flex items-center justify-center gap-2">
              <XCircle className="w-4 h-4" />
              거절된 견적서입니다
            </p>
          </div>
        )}
      </div>

      {/* 확인 모달 */}
      {showAcceptConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-2">견적서 수락</h3>
            <p className="text-gray-600 mb-4">
              {formatProposer(
                estimate.teams,
                (estimate as any).proposerType as 'TEAM' | 'INDIVIDUAL' | undefined,
                (estimate as any).proposerName as string | null | undefined
              )}과 계약을 진행합니다.
            </p>
            <div className="mb-4 rounded-lg border border-blue-100 bg-blue-50 p-3">
              <p className="text-xs font-semibold text-blue-800 mb-2">수락 후 진행 단계</p>
              <ol className="text-xs text-blue-900 space-y-1 list-decimal list-inside">
                <li>계약 생성</li>
                <li>1차 마일스톤 결제</li>
                <li>킥오프 및 작업 시작</li>
              </ol>
            </div>
            <div className="mb-6 flex items-start gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
              <Checkbox
                id="shouldCloseProject"
                checked={shouldCloseProject}
                onCheckedChange={(checked) => setShouldCloseProject(checked === true)}
                className="mt-0.5"
              />
              <label
                htmlFor="shouldCloseProject"
                className="text-sm text-gray-700 cursor-pointer leading-relaxed flex-1"
              >
                이 프로젝트의 모집을 마감합니다. (체크 시 프로젝트 상태가 '매칭 완료'로 변경되어 더 이상 지원을 받지 않습니다. 체크 해제 시 '모집 중' 상태가 유지됩니다.)
              </label>
            </div>
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => {
                  setShowAcceptConfirm(false)
                  setShouldCloseProject(false)
                }}
                className="flex-1"
                disabled={processing}
              >
                취소
              </Button>
              <Button
                onClick={handleAccept}
                className="flex-1 bg-blue-600 hover:bg-blue-700"
                disabled={processing}
              >
                {processing ? '처리 중...' : '수락하기'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {showRejectConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-2">견적을 거절할까요</h3>
            <p className="text-sm text-gray-600 mb-3">사유를 선택해 주세요 (선택).</p>
            <div className="mb-4 grid grid-cols-2 gap-2">
              {['예산', '일정', '범위', '기타'].map((reason) => (
                <button
                  key={reason}
                  type="button"
                  onClick={() => setRejectReason(reason)}
                  className={`rounded-md border px-3 py-2 text-sm ${rejectReason === reason ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-200 text-gray-700 hover:bg-gray-50'}`}
                >
                  {reason}
                </button>
              ))}
            </div>
            <p className="text-xs text-gray-500 mb-4">거절해도 상대에게 상세 사유는 전달되지 않아요.</p>
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setShowRejectConfirm(false)} className="flex-1" disabled={processing}>
                취소
              </Button>
              <Button onClick={handleReject} variant="destructive" className="flex-1" disabled={processing}>
                {processing ? '처리 중...' : '거절 확정'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
